import { useState, useEffect, useRef, useCallback } from 'react';
import './AccessibilityWidget.css';

const STORAGE_KEY = 'a11y-settings';

const DEFAULTS = {
  fontSize: 0,
  highContrast: false,
  highlightLinks: false,
  dyslexiaFont: false,
  reducedMotion: false,
};

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch {
    return { ...DEFAULTS };
  }
}

function save(settings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(load);
  const panelRef = useRef(null);
  const toggleRef = useRef(null);

  /* ── Apply settings to the DOM ── */
  const apply = useCallback((s) => {
    const root = document.documentElement;

    // Font size: base 100%, step 12.5%
    root.style.fontSize = s.fontSize === 0 ? '' : `${100 + s.fontSize * 12.5}%`;

    // Toggle classes
    root.classList.toggle('a11y-high-contrast', s.highContrast);
    root.classList.toggle('a11y-highlight-links', s.highlightLinks);
    root.classList.toggle('a11y-dyslexia-font', s.dyslexiaFont);
    root.classList.toggle('a11y-reduced-motion', s.reducedMotion);
  }, []);

  // Apply on mount + whenever settings change
  useEffect(() => {
    apply(settings);
    save(settings);
  }, [settings, apply]);

  // Close panel on Escape or outside click
  useEffect(() => {
    if (!open) return;

    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }

    function handleClick(e) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open]);

  const update = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));
  const toggle = (key) => update(key, !settings[key]);
  const resetAll = () => setSettings({ ...DEFAULTS });

  const adjustFont = (delta) => {
    setSettings((prev) => ({
      ...prev,
      fontSize: Math.min(4, Math.max(-2, prev.fontSize + delta)),
    }));
  };

  return (
    <div className="a11y-widget">
      <button
        ref={toggleRef}
        className="a11y-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label="Accessibility settings"
        aria-expanded={open}
      >
        {/* Universal accessibility / wheelchair icon */}
        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28" aria-hidden="true">
          <path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm9 5H3v2h6.5v4l-2 7h2.2l1.8-6.3L13.3 20h2.2l-2-7V9H20V7Z"/>
        </svg>
      </button>

      {open && (
        <div className="a11y-panel" ref={panelRef} role="dialog" aria-label="Accessibility settings">
          <div className="a11y-panel__header">
            <h2 className="a11y-panel__title">Accessibility</h2>
            <button className="a11y-panel__close" onClick={() => setOpen(false)} aria-label="Close">
              &times;
            </button>
          </div>

          {/* Font Size */}
          <div className="a11y-font-size">
            <span className="a11y-font-size__label">Font Size</span>
            <div className="a11y-font-size__controls">
              <button
                className="a11y-font-size__btn"
                onClick={() => adjustFont(-1)}
                disabled={settings.fontSize <= -2}
                aria-label="Decrease font size"
              >
                A&minus;
              </button>
              <span className="a11y-font-size__value">
                {settings.fontSize > 0 ? `+${settings.fontSize}` : settings.fontSize}
              </span>
              <button
                className="a11y-font-size__btn"
                onClick={() => adjustFont(1)}
                disabled={settings.fontSize >= 4}
                aria-label="Increase font size"
              >
                A+
              </button>
            </div>
          </div>

          {/* Toggles */}
          {[
            ['highContrast', 'High Contrast'],
            ['highlightLinks', 'Highlight Links'],
            ['dyslexiaFont', 'Dyslexia Font'],
            ['reducedMotion', 'Reduce Motion'],
          ].map(([key, label]) => (
            <div className="a11y-option" key={key}>
              <span className="a11y-option__label">{label}</span>
              <label className="a11y-switch">
                <input
                  type="checkbox"
                  checked={settings[key]}
                  onChange={() => toggle(key)}
                  aria-label={label}
                />
                <span className="a11y-switch__slider" />
              </label>
            </div>
          ))}

          {/* Reset */}
          <button className="a11y-reset" onClick={resetAll}>
            Reset All
          </button>
        </div>
      )}
    </div>
  );
}
